import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import {
  GiBasketballBall,
  GiSoccerBall,
  GiTennisBall,
  GiVolleyballBall,
  GiAmericanFootballBall,
  GiBaseballGlove
} from 'react-icons/gi';
import { AnimatePresence, motion } from 'framer-motion';
import useLogin from '../../hooks/auth/useLogin';
import useToast from '../../hooks/ui/useToast';
import { loginSchema } from '../../validations/loginValidation';
import BackButton from '../BackButton/BackButton';
import ToastPopup from '../ToastPopup/ToastPopup';
import styles from './LoginForm.module.css';
import globalButtonStyles from '../../styles/ButtonGradient.module.css';

const inputFields = [{ type: 'email', placeholder: 'Email', name: 'email' }];

const sportIcons = [
  <GiBasketballBall />,
  <GiAmericanFootballBall />,
  <GiSoccerBall />,
  <GiVolleyballBall />,
  <GiTennisBall />,
  <GiBaseballGlove />
];

const LoginForm = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [currentIconIndex, setCurrentIconIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const { login } = useLogin();
  const { toast, showToast, closeToast } = useToast(3000);

  const validateFormData = async () => {
    try {
      await loginSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return null;
    } catch (err) {
      const errors = {};
      if (err.inner && err.inner.length > 0) {
        err.inner.forEach((e) => {
          errors[e.path] = e.message;
        });
      } else {
        errors.general = err.message;
      }
      setFormErrors(errors);
      return err.message;
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    try {
      await loginSchema.validateAt(name, { ...formData, [name]: value });
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (err) {
      setFormErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const validationError = await validateFormData();
    if (validationError) {
      showToast(validationError, 'error');
      setLoading(false);
      return;
    }

    try {
      const response = await login(formData);
  
      if (response) {
        showToast('Login successful! Welcome back. 🏆', 'success');
      }
    } catch (error) {
      console.error('Login error:', error);
  
      const errorMessage = 
        error?.graphQLErrors?.[0]?.message || 
        error?.message || 
        'Login failed. Please check your credentials and try again.';
  
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIconIndex((prev) => (prev + 1) % sportIcons.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={styles.wrapper}>
      <BackButton />
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.iconCircle}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIconIndex}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.5 }}
              className={styles.icon}
            >
              {sportIcons[currentIconIndex]}
            </motion.div>
          </AnimatePresence>
        </div>
        {inputFields.map((field) => (
          <div key={field.name} className={styles.inputWrapper}>
            <input
              type={field.type}
              placeholder={field.placeholder}
              name={field.name}
              className={styles.input}
              value={formData[field.name]}
              onChange={handleChange}
              required
            />
            {formErrors[field.name] && (
              <div className={styles.fieldError}>{formErrors[field.name]}</div>
            )}
          </div>
        ))}
        <div className={styles.passwordWrapper}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            name="password"
            className={styles.input}
            value={formData.password}
            onChange={handleChange}
            required
          />
          <button
            type="button"
            className={styles.eyeButton}
            onClick={togglePasswordVisibility}
            aria-label="Toggle password visibility"
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
        {formErrors.password && (
          <div className={styles.fieldError}>{formErrors.password}</div>
        )}
        <div className={styles.actions}>
          <Link to="/forgot-password" className={styles.forgotLink}>
            Forgot Password?
          </Link>
        </div>
        <button
          type="submit"
          className={`${globalButtonStyles.button} ${styles.loginBtn}`}
          disabled={loading}
        >
          {loading ? 'Logging in...' : 'Log In'}
        </button>
      </form>
      <ToastPopup
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={closeToast}
      />
    </div>
  );
};

export default LoginForm;